import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, Linkedin, Twitter, Instagram, Youtube, ArrowRight } from "lucide-react";
import Logo from "./Logo";

const MAP = "https://static.prod-images.emergentagent.com/jobs/60fe7cfd-579c-48af-b4a9-bdfd3f1b809e/images/bf5b5136019b4b043d8fe5991bb61df5a36b61cef5a03cbb3e1c83f7247ce98a.jpeg";

const QUICK = ["about", "programs", "knowledge", "opportunities", "events", "collaborate", "professionals"];
const SOCIAL = [
  { Icon: Linkedin, label: "LinkedIn" },
  { Icon: Twitter, label: "X" },
  { Icon: Instagram, label: "Instagram" },
  { Icon: Youtube, label: "YouTube" },
];

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const programs = ["p1", "p2", "p3"].map((k) => t(`programs.${k}.title`));

  return (
    <footer data-testid="site-footer" className="panel-forest">
      <img
        src={MAP}
        alt=""
        aria-hidden="true"
        className="absolute -bottom-10 ltr:-right-10 rtl:-left-10 w-[520px] max-w-[70vw] opacity-50 mix-blend-multiply [mask-image:radial-gradient(closest-side,black_40%,transparent)] pointer-events-none select-none"
      />
      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 pt-20 pb-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Logo light size="lg" />
          <p className="mt-6 text-white/70 max-w-sm text-[15px] leading-relaxed">{t("footer.about")}</p>
          <div className="mt-7 flex items-center gap-2.5">
            {SOCIAL.map(({ Icon, label }) => (
              <a
                key={label}
                href="#"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="h-9 w-9 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:bg-gold hover:border-gold hover:text-white transition-colors"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="overline text-gold-light">{t("footer.quick")}</div>
          <ul className="mt-5 space-y-2.5 text-sm">
            {QUICK.map((k) => (
              <li key={k}>
                <Link
                  to={k === "knowledge" ? "/house-of-wisdom" : `/${k}`}
                  data-testid={`footer-nav-${k}`}
                  className="text-white/70 hover:text-gold-light transition-colors"
                >
                  {t(`nav.${k}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="overline text-gold-light">{t("footer.programs")}</div>
          <ul className="mt-5 space-y-2.5 text-sm">
            {programs.map((p) => (
              <li key={p}>
                <Link to="/programs" className="text-white/70 hover:text-gold-light transition-colors">{p}</Link>
              </li>
            ))}
            <li><Link to="/join" data-testid="footer-nav-join" className="text-white/70 hover:text-gold-light transition-colors">{t("nav.join")}</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <div className="overline text-gold-light">{t("footer.contact")}</div>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a href="mailto:geneticsxresearcher@gmail.com" data-testid="footer-email-link" className="flex items-center gap-3 text-white/80 hover:text-gold-light transition-colors">
                <Mail className="h-4 w-4 text-gold-light" /> geneticsxresearcher@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-3 text-white/70">
              <MapPin className="h-4 w-4 text-gold-light" /> {t("footer.location")}
            </li>
          </ul>
          <Link to="/contact" data-testid="footer-newsletter-cta" className="btn-gold-outline !border-gold-light !text-white mt-7 !py-2.5 !px-5 !text-[11px]">
            {t("footer.newsletter_cta")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
          </Link>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-5 flex flex-col md:flex-row gap-2 items-center justify-between text-xs text-white/50">
          <div>© {year} GXR · {t("footer.credit")}</div>
          <div className="text-white/40">{t("brand.tagline")}</div>
        </div>
      </div>
    </footer>
  );
}
